<script lang="ts">
  import type { Snippet } from 'svelte';

  let { timeline, detail, chat, chatOpen = $bindable(false) }: {
    timeline: Snippet;
    detail: Snippet;
    chat: Snippet;
    chatOpen?: boolean;
  } = $props();

  let containerEl: HTMLElement;
  let leftWidth = $state(280);
  let rightWidth = $state(380);
  let dragging = $state<'left' | 'right' | null>(null);

  const MIN_PANEL = 180;
  const MIN_CENTER = 300;
  const HANDLE_WIDTH = 6;

  function onPointerDown(handle: 'left' | 'right', e: PointerEvent) {
    e.preventDefault();
    dragging = handle;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: PointerEvent) {
    if (!dragging || !containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const total = rect.width - HANDLE_WIDTH * (chatOpen ? 2 : 1);

    if (dragging === 'left') {
      const maxLeft = chatOpen ? total - rightWidth - MIN_CENTER : total - MIN_CENTER;
      leftWidth = Math.max(MIN_PANEL, Math.min(x, maxLeft));
    } else if (chatOpen) {
      const fromRight = rect.width - (e.clientX - rect.left);
      rightWidth = Math.max(MIN_PANEL, Math.min(fromRight, total - leftWidth - MIN_CENTER));
    }
  }

  function onPointerUp() {
    dragging = null;
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  bind:this={containerEl}
  class="flex h-[calc(100vh-57px)] select-none relative"
  class:cursor-col-resize={dragging}
  onpointermove={onPointerMove}
  onpointerup={onPointerUp}
  onpointerleave={onPointerUp}
>
  <!-- Timeline Panel -->
  <div data-panel class="overflow-y-auto shrink-0" style="width: {leftWidth}px">
    {@render timeline()}
  </div>

  <!-- Left resize handle -->
  <div
    class="shrink-0 w-1.5 cursor-col-resize group relative"
    onpointerdown={(e) => onPointerDown('left', e)}
    role="separator"
    aria-orientation="vertical"
    aria-label="Resize timeline panel"
    tabindex={0}
  >
    <div class="absolute inset-y-0 left-0 w-full bg-border transition-colors duration-150 {dragging === 'left' ? 'bg-accent' : 'group-hover:bg-accent/50'}"></div>
  </div>

  <!-- Detail Panel -->
  <div data-panel class="overflow-y-auto flex-1 min-w-0">
    {@render detail()}
  </div>

  {#if chatOpen}
    <!-- Right resize handle -->
    <div
      class="shrink-0 w-1.5 cursor-col-resize group relative"
      onpointerdown={(e) => onPointerDown('right', e)}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize chat panel"
      tabindex={0}
    >
      <div class="absolute inset-y-0 left-0 w-full bg-border transition-colors duration-150 {dragging === 'right' ? 'bg-accent' : 'group-hover:bg-accent/50'}"></div>
    </div>

    <!-- Chat Panel -->
    <div data-panel class="overflow-y-auto shrink-0 animate-slide-in-right" style="width: {rightWidth}px">
      {@render chat()}
    </div>
  {/if}

  <!-- Chat toggle button -->
  {#if !chatOpen}
    <button
      onclick={() => chatOpen = true}
      class="absolute right-0 top-1/2 -translate-y-1/2 bg-accent hover:bg-accent-hover text-white px-2 py-4 rounded-l-lg shadow-lg transition-all duration-200 hover:px-3 group"
      aria-label="Open AI Debugger chat"
    >
      <div class="flex flex-col items-center gap-1">
        <span class="text-lg">🤖</span>
        <span class="text-[10px] font-semibold tracking-wide [writing-mode:vertical-lr] rotate-180">AI Chat</span>
      </div>
    </button>
  {/if}
</div>

<style>
  @keyframes slide-in-right {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  .animate-slide-in-right {
    animation: slide-in-right 0.25s ease-out;
  }
</style>
